import {
  Suspense,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./SaledItems.css";
import { Button, Pencil, Trash2, CircleX, Table, Card } from "../ui";
import FilterGroup from "../analysis/filter/FilterUI";
import { translations } from "../../utils/lang";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import {
  deleteSaledItemAPI,
  fetchSaledItemsAPI,
} from "../../utils/api/saledItems";
import { DeleteModal } from "../modals/Delete";
import {
  APPS,
  getItemKey,
  getItemName,
  getPeriodLabel,
  getRowTotal,
} from "./saledItemsUtils";
import { integrationLabel } from "../../utils/integrations";

const AddSaledItems = lazy(() => import("./add/AddSaledItems"));

const BASIC_FILTERS = {
  store: [],
  item: [],
  period: [],
  app: [],
};

const TEXT = {
  ar: {
    addItems: "إضافة أصناف مباعة",
    closeForm: "إغلاق النموذج",
    deleteFiltered: "حذف جميع السجلات المفلترة",
    deleteDescription: (count) =>
      `سيتم حذف ${count} سجل نهائياً: كل السجلات المطابقة للفلاتر الحالية.`,
    deleteAllWarning: "لا توجد فلاتر مطبقة، لذلك سيتم حذف جميع السجلات.",
    deleteFailed: "تعذر الحذف. حاول مرة أخرى.",
    deleteNotAllowed: "لا تملك صلاحية حذف بعض هذه السجلات.",
    loading: "جارٍ تحميل الأصناف المباعة…",
    count: (shown, total) => `${shown} من ${total} سجل`,
  },
  en: {
    addItems: "Add sold items",
    closeForm: "Close form",
    deleteFiltered: "Delete all filtered records",
    deleteDescription: (count) =>
      `This permanently deletes ${count} record(s): every record matching the current filters.`,
    deleteAllWarning: "No filters are applied, so this deletes every record.",
    deleteFailed: "The records could not be deleted. Please try again.",
    deleteNotAllowed: "You are not allowed to delete some of these records.",
    loading: "Loading sold items…",
    count: (shown, total) => `${shown} of ${total} records`,
  },
};

const numberFormat = new Intl.NumberFormat("en-US");
const renderCount = (key) => (row) => (
  <span className="saled-items-num">{numberFormat.format(row[key])}</span>
);

export default function SaledItems() {
  const {
    lang,
    storeAccess,
    handleTokenExpiration,
  } = useContext(Context);
  const { can, has } = useAccess();
  const text = TEXT[lang] || TEXT.en;

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [saledItems, setSaledItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(BASIC_FILTERS);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  // The context recreates these on every provider render; a ref keeps the
  // fetcher stable so the initial load runs once.
  const contextRef = useRef({ storeAccess, handleTokenExpiration });
  contextRef.current = { storeAccess, handleTokenExpiration };
  const requestRef = useRef(0);

  // Everything derived from a record, computed once per load instead of
  // again for every filter pass and every sort comparison.
  const rows = useMemo(
    () =>
      saledItems.map((row) => ({
        row,
        storeKey: String(row.storeName || "").toLowerCase(),
        itemKey: getItemKey(row),
        periodLabel: getPeriodLabel(row),
        total: getRowTotal(row),
      })),
    [saledItems],
  );

  const allStores = useMemo(
    () => [...new Set(rows.map((r) => r.storeKey).filter(Boolean))],
    [rows],
  );

  const allItems = useMemo(
    () => [...new Set(rows.map((r) => r.itemKey).filter(Boolean))],
    [rows],
  );

  const allPeriods = useMemo(
    () => [...new Set(rows.map((r) => r.periodLabel).filter(Boolean))],
    [rows],
  );

  const handleFilterChange = (key, values) => {
    setFilters((prev) => ({ ...prev, [key]: values }));
  };

  const clearFilters = () => setFilters(BASIC_FILTERS);

  const fetchSaledItems = useCallback(async () => {
    const { storeAccess: access, handleTokenExpiration: expire } =
      contextRef.current;
    const token = localStorage.getItem("auth-token");
    if (!token) {
      expire();
      return;
    }

    // Only the latest request may write state (e.g. a refresh after an
    // upload racing an earlier load).
    const requestId = ++requestRef.current;
    try {
      const data = await fetchSaledItemsAPI(token, access);
      if (requestId !== requestRef.current) return;
      // Existing behaviour: the helper can't tell an expired session from
      // other failures, and a failed load is treated as expiry.
      if (data) setSaledItems(data);
      else expire();
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSaledItems();
  }, [fetchSaledItems]);

  const filteredRows = useMemo(() => {
    return rows
      .filter(({ row, storeKey, itemKey, periodLabel }) => {
        if (filters.store.length && !filters.store.includes(storeKey)) {
          return false;
        }

        if (filters.item.length && !filters.item.includes(itemKey)) {
          return false;
        }

        if (filters.period.length && !filters.period.includes(periodLabel)) {
          return false;
        }

        if (filters.app.length) {
          const hasAppSales = filters.app.some((app) => Number(row[app]) > 0);
          if (!hasAppSales) return false;
        }

        return true;
      })
      .sort((a, b) => b.total - a.total);
  }, [rows, filters]);

  const columns = useMemo(
    () => [
      { key: "storeName", label: translations[lang].SaledItems.headers[0] },
      { key: "itemName", label: translations[lang].SaledItems.headers[1] },
      { key: "period", label: translations[lang].SaledItems.headers[2] },
      ...APPS.map((app) => ({
        key: app,
        label: integrationLabel(app, lang),
        render: renderCount(app),
      })),
      {
        key: "total",
        label: translations[lang].SaledItems.headers[9],
        render: renderCount("total"),
      },
    ],
    [lang],
  );

  const tableData = useMemo(
    () =>
      filteredRows.map(({ row, total }) => ({
        ...row,
        // Missing counts display as 0, matching how they are summed.
        ...Object.fromEntries(APPS.map((app) => [app, Number(row[app] || 0)])),
        itemName: getItemName(row),
        period: `${row.fromDate} → ${row.toDate}`,
        total,
      })),
    [filteredRows],
  );

  const hasActiveFilters = Object.values(filters).some((v) => v.length);

  const deleteByIds = async () => {
    if (!has("saledItem.delete")) return;
    const token = localStorage.getItem("auth-token");
    const ids = filteredRows.map(({ row }) => row._id);

    if (!ids.length) return;

    // Every record about to be deleted must belong to a restaurant the user may delete
    // for (`sId` is the restaurant's public id). One record outside the scope, or with
    // no usable `sId`, stops the whole deletion before any request is sent (NA-011).
    const allInScope = filteredRows.every(({ row }) =>
      can("saledItem.delete", { restaurantId: row.sId }),
    );
    if (!allInScope) {
      setDeleteError(text.deleteNotAllowed);
      return;
    }

    if (token) {
      setDeleteError("");
      const result = await deleteSaledItemAPI(token, ids);

      if (result) {
        setDeleteTarget(null);
        fetchSaledItems();
      } else {
        // Keep the dialog open so the failure is visible and can be retried.
        setDeleteError(text.deleteFailed);
      }
    } else {
      handleTokenExpiration();
    }
  };

  return (
    <div className="menu-container saled-items">
      {has("saledItem.create") && (
        <div className="menu-header-row">
          <Button
            variant="primary"
            appearance="soft"
            size="sm"
            rounded
            icon={isFormOpen ? <CircleX size={18} /> : <Pencil size={18} />}
            aria-label={isFormOpen ? text.closeForm : text.addItems}
            title={isFormOpen ? text.closeForm : text.addItems}
            aria-expanded={isFormOpen}
            onClick={() => setIsFormOpen((prev) => !prev)}
          />
        </div>
      )}

      {isFormOpen && (
        <Suspense fallback={null}>
          <AddSaledItems
            onRefresh={() => {
              fetchSaledItems();
              setIsFormOpen(false);
            }}
          />
        </Suspense>
      )}
      <Card className="campains-filter-card" hoverable={false}>
        <div className="campains-filter">
          <FilterGroup
            label={lang === "ar" ? "المتجر - الكل" : "Store - All"}
            options={allStores}
            selected={filters.store}
            onChange={(v) => handleFilterChange("store", v)}
          />

          <FilterGroup
            label={lang === "ar" ? "الصنف - الكل" : "Item - All"}
            options={allItems}
            selected={filters.item}
            onChange={(v) => handleFilterChange("item", v)}
          />

          <FilterGroup
            label={lang === "ar" ? "الشهر - الفترة" : "Month / Period"}
            options={allPeriods}
            selected={filters.period}
            onChange={(v) => handleFilterChange("period", v)}
          />

          <FilterGroup
            label={lang === "ar" ? "التطبيق - الكل" : "App - All"}
            options={APPS}
            selected={filters.app}
            onChange={(v) => handleFilterChange("app", v)}
            getLabel={(app) => integrationLabel(app, lang)}
          />

          <Button
            variant="danger"
            appearance="soft"
            size="sm"
            onClick={clearFilters}
          >
            {translations[lang].reports.details.inputTitles[6]}
          </Button>

          {has("saledItem.delete") && (
            <div className="saled-items-delete">
              <Button
                variant="danger"
                appearance=""
                size="sm"
                rounded
                icon={<Trash2 size={18} />}
                aria-label={text.deleteFiltered}
                title={text.deleteFiltered}
                disabled={!filteredRows.length}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteError("");
                  setDeleteTarget(true);
                }}
              />
            </div>
          )}
        </div>
      </Card>

      {!loading && (
        <p className="saled-items-count" role="status">
          {text.count(filteredRows.length, rows.length)}
        </p>
      )}

      <Table
        columns={columns}
        data={tableData}
        loading={loading}
        emptyText={translations[lang].noData}
      />

      {deleteTarget && (
        <DeleteModal
          cancel={() => {
            setDeleteTarget(null);
            setDeleteError("");
          }}
          id={deleteTarget}
          error={deleteError}
          description={`${text.deleteDescription(filteredRows.length)}${
            hasActiveFilters ? "" : ` ${text.deleteAllWarning}`
          }`}
          handleDelete={deleteByIds}
        />
      )}
    </div>
  );
}
